import { ContentState, EditorState } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
// @ts-ignore
import htmlToDraft from "html-to-draftjs";
import moment from "moment";
import { EffectCallback, useEffect } from "react";
// @ts-ignore
import { toolbarControlTypes } from "react-mui-draft-wysiwyg";
import AuthData from "../model/AuthData";
import ArticleDto from "../model/manuscript/ArticleDto";
import EnumActionType from "./enum/EnumActionType";
import EnumDesignation from "./enum/EnumDesignation";
import EnumGroup from "./enum/EnumGroup";
import EnumReviewStatus from "./enum/EnumReviewStatus";
import EnumTicketStatus from "./enum/EnumTicketStatus";
import EnumTicketType from "./enum/EnumTicketType";
import EnumYesNo from "./enum/EnumYesNo";
import EnumFileType from "./EnumFileType";
import MessageType from "./MessageType";

const axiosConfig = (token: string, params?: Object) => {
  return {
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
    params: params ? params : {},
  };
};

const handleSuccessMessage = (showMessage: Function, response: any) => {
  if (response.data.successes) {
    response.data.successes.forEach((e: any) => {
      showMessage(e.message, MessageType.SUCCESS);
    });
  } else {
    showMessage(response.toString(), MessageType.SUCCESS);
  }
};

const handleErrorMessage = (showMessage: any, error: any) => {
  if (error?.response?.status === 401) {
    showMessage("Your previous session has expired. Please login.", MessageType.ERROR);
  } else if (error?.response?.data?.message) {
    showMessage(error.response.data.message, MessageType.ERROR);
  } else {
    showMessage(error.toString(), MessageType.ERROR);
  }
};

const validateEmail = (email: string) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

const existValue = (value: string): boolean => {
  return value && value !== "" ? true : false;
};

const sortArticleArray = (array: any) => {
  return array.sort((a: any, b: any) => b.createDate - a.createDate);
};

const getTypeFromNameFile = (filename: string) => {
  const fileArray = filename.split(".");
  return fileArray[fileArray.length - 1];
};

// eslint-disable-next-line
const useEffectOnce = (effect: EffectCallback) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(effect, []);
};

const richTextStateToHtml = (state: any) => {
  const content = state.getCurrentContent();
  return content.hasText() ? stateToHTML(content) : "";
};

const richTextStateFromHTML = (html: string) => {
  const contentBlock = htmlToDraft(html.replaceAll("\t", ""));
  const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
  return EditorState.createWithContent(contentState);
};

const parseArticleForBackend = (article: ArticleDto) => {
  const {
    type,
    firstTime,
    coverLetter,
    keywords,
    abstractText,
    reviewerOne,
    reviewerTwo,
    reviewerRemoved,
    authorsIds,
  } = article;

  let newArticle = { ...article };
  //1. step
  newArticle.type = type ? type : null;
  newArticle.firstTime = firstTime ? firstTime.code === EnumYesNo.YES : null;
  newArticle.coverLetter = richTextStateToHtml(coverLetter);
  newArticle.keywords = keywords ? keywords.map((k: any) => k.keyword).join(";") : "";
  newArticle.abstractText = richTextStateToHtml(abstractText);
  //2. step
  newArticle.authorsIds = authorsIds ? authorsIds : [];
  //3. step
  newArticle.reviewerOne = reviewerOne ? reviewerOne.code : null;
  newArticle.reviewerTwo = reviewerTwo ? reviewerTwo.code : null;
  newArticle.reviewerRemoved = reviewerRemoved ? reviewerRemoved.code : null;

  return newArticle;
};

const getTicketStatus = (status: any, Labels: any) => {
  switch (status) {
    case EnumTicketStatus.AWAITING_RESPONSE:
      return Labels.AWAITING_RESPONSE;
    case EnumTicketStatus.CLOSED:
      return Labels.CLOSED;
    case EnumTicketStatus.OPEN:
      return Labels.OPEN;
  }
};

const getTicketType = (type: any, Labels: any) => {
  switch (type) {
    case EnumTicketType.BUG:
      return Labels.BUG;
    case EnumTicketType.GENERAL:
      return Labels.GENERAL;
    case EnumTicketType.REQUEST:
      return Labels.REQUEST;
  }
};

const getReviewStatus = (status: any, Labels: any) => {
  switch (status) {
    case EnumReviewStatus.DRAFT:
      return Labels.DRAFT;
    case EnumReviewStatus.NEW_ARTICLE:
      return Labels.NEW_ARTICLE;
    case EnumReviewStatus.REJECTED:
      return Labels.REJECTED;
    case EnumReviewStatus.ACCEPTED:
      return Labels.ACCEPTED;
    case EnumReviewStatus.MAJOR_REVISION:
      return Labels.MAJOR_REVISION;
    case EnumReviewStatus.MINOR_REVISION:
      return Labels.MINOR_REVISION;
    case EnumReviewStatus.REVIEWED:
      return Labels.REVIEWED;
    case EnumReviewStatus.UNDER_REVIEW:
      return Labels.UNDER_REVIEW;
    case EnumReviewStatus.REVIEWER_SELECTION:
      return Labels.REVIEWER_SELECTION;
    case EnumReviewStatus.DIRECT_REJECT:
      return Labels.DIRECT_REJECT;
    case EnumReviewStatus.CORRECTION:
      return Labels.CORRECTION;
    case EnumReviewStatus.REJECTED_R0:
      return Labels.REJECTED_R0;
    case EnumReviewStatus.DIRECT_REJECT_R0:
      return Labels.DIRECT_REJECT_R0;
    case EnumReviewStatus.REJECT_AND_RESUBMIT_AS_A_LETTER_TO_THE_EDITOR:
      return Labels.REJECT_AND_RESUBMIT_AS_A_LETTER_TO_THE_EDITOR;
    case EnumReviewStatus.ASSOCIATE_EDITOR:
      return Labels.ASSOCIATE_EDITOR;
    case EnumReviewStatus.RETURNED_TO_EDITOR:
      return Labels.RETURNED_TO_EDITOR;
    default:
      return "None";
  }
};

const getActionType = (actionType: any, Labels: any) => {
  switch (actionType) {
    case EnumActionType.STATUS_CHANGE:
      return Labels.STATUS_CHANGE;
    case EnumActionType.REVIEWER_ASSIGNMENT:
      return Labels.REVIEWER_ASSIGNMENT;
    case EnumActionType.REVIEWER_ASSIGNMENT_REMOVED:
      return Labels.REVIEWER_ASSIGNMENT_REMOVED;
    case EnumActionType.REVIEWER_MAILED:
      return Labels.REVIEWER_MAILED;
    case EnumActionType.REVIEWER_ANSWER:
      return Labels.REVIEWER_ANSWER;
    case EnumActionType.REVIEWER_DECISION:
      return Labels.REVIEWER_DECISION;
    case EnumActionType.AE_ASSIGNMENT:
      return Labels.AE_ASSIGNMENT;
    case EnumActionType.AE_DECISION:
      return Labels.AE_DECISION;
    case EnumActionType.ASSISTANT_DECISION:
      return Labels.ASSISTANT_DECISION;
    case EnumActionType.EDITOR_DECISION:
      return Labels.EDITOR_DECISION;
    default:
      return EnumActionType.EDITOR_DECISION;
  }
};

const getDesignationType = (type: any, Labels: any) => {
  switch (type) {
    case EnumDesignation.ADDITIONAL_FILE:
      return "Additional file";
    case EnumDesignation.FIGURE:
      return "Figure";
    case EnumDesignation.SUPPLEMENTARY_FIGURE:
      return "Supplementary Figure";
    case EnumDesignation.FIGURE_PDF:
      return "Figure PDF";
    case EnumDesignation.FULL_MANUSCRIPT:
      return "Full manuscript";
    case EnumDesignation.LETTER_OF_REBUTAL:
      return "Letter of Rebuttal";
    case EnumDesignation.LETTER_OF_REBUTAL_PDF:
      return "Letter of Rebuttal PDF";
    case EnumDesignation.MANUSCRIPT:
      return Labels.MANUSCRIPT;
    case EnumDesignation.MARKED_MANUSCRIPTS:
      return "Marked manuscripts";
    case EnumDesignation.MARKED_MANUSCRIPTS_PDF:
      return "Marked manuscript PDF";
    case EnumDesignation.OVERVIEW:
      return Labels.OVERVIEW;
    case EnumDesignation.TABLES:
      return Labels.TABLES;
    case EnumDesignation.UNMARKED_MANUSCRIPT:
      return "Unmarked Manuscript PDF";
    case EnumDesignation.VIDEO_FILE:
      return Labels.VIDEO_FILE;
    case EnumDesignation.FULL_ARTICLE:
      return "Full Article";
    case EnumDesignation.COVER_LETTER:
      return "Cover letter";
    case EnumDesignation.ABSTRACT:
      return "Abstract";
    default:
      return "";
  }
};

export const handleFileType = (code: string) => {
  switch (code) {
    case EnumDesignation.MANUSCRIPT:
    case EnumDesignation.TABLES:
    case EnumDesignation.LETTER_OF_REBUTAL:
    case EnumDesignation.MARKED_MANUSCRIPTS:
      return EnumFileType.MANUSCRIPT;
    case EnumDesignation.UNMARKED_MANUSCRIPT:
    case EnumDesignation.ADDITIONAL_FILE:
      return EnumFileType.ADDITIONAL;
    case EnumDesignation.FIGURE:
    case EnumDesignation.SUPPLEMENTARY_FIGURE:
      return EnumFileType.FIGURE;
    case EnumDesignation.VIDEO_FILE:
      return EnumFileType.VIDEO;
    default:
      return undefined;
  }
};

const parseDate = (rowDate: any) => {
  if (!rowDate) {
    return "Unknown";
  }
  let month = rowDate[1] < 10 ? "0" + rowDate[1] : rowDate[1];
  let day = rowDate[2] < 10 ? "0" + rowDate[2] : rowDate[2];
  let hour = rowDate[3] < 10 ? "0" + rowDate[3] : rowDate[3];
  let minute = rowDate[4] < 10 ? "0" + rowDate[4] : rowDate[4];
  let second = rowDate[5] && rowDate[5] < 10 ? "0" + rowDate[5] : rowDate[5];
  const date = [rowDate[0], month - 1, day, hour, minute, second ?? 0];
  return moment(date).fromNow();
};

const isUserAssociateEditor = (authData?: AuthData) => {
  const userGroup = authData?.user?.group ? authData.user.group : null;
  return userGroup && userGroup.group === EnumGroup.ASSOCIATE_EDITOR;
};

const dateView = (date: any) => {
  if (!date) return undefined;
  return `${date[0]}-${date[1]}-${date[2]}`;
};

export const dateViewDMY = (date: number[]) => {
  if (!date) return undefined;
  return `${date[2]}/${date[1]}/${date[0]}`;
};

const dateTimeView = (rowDate: any) => {
  if (!rowDate) return undefined;
  let month = rowDate[1] < 10 ? "0" + rowDate[1] : rowDate[1];
  let day = rowDate[2] < 10 ? "0" + rowDate[2] : rowDate[2];
  let hour = rowDate[3] < 10 ? "0" + rowDate[3] : rowDate[3];
  let minute = rowDate[4] < 10 ? "0" + rowDate[4] : rowDate[4];
  let second = rowDate[5] && rowDate[5] < 10 ? "0" + rowDate[5] : rowDate[5];
  const date = [rowDate[0], month - 1, day, hour, minute, second ?? 0];
  return moment(date).format("YYYY-MM-DD HH:mm:ss");
};

export const dateFormatView = (date?: any) => moment(`${date[2]}.${date[1]}.${date[0]}`, "DD.MM.YYYY");

const preventDefaultEnter = (keyEvent: any) => {
  if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
    keyEvent.preventDefault();
  }
};

const renameFile = (originalFile: File, newName: string) =>
  new File([originalFile], newName, {
    type: originalFile.type,
    lastModified: originalFile.lastModified,
  });

export const renameFileName = (originalName: string, addString: any) => {
  const name = originalName.split(".");

  return `${name[0]}-${addString}.${name[1]}`;
};

const richTextValue = (ref: any) => ref.current.get();

const uniqueBy = (arr: any, prop: any) => {
  return arr.reduce((a: any, d: any) => {
    if (!a.includes(d[prop])) {
      a.push(d[prop]);
    }
    return a;
  }, []);
};

export const nextLetterInAlphabet = (letter: string) => {
  if (letter === "z") {
    return "a";
  } else if (letter === "Z") {
    return "A";
  } else {
    return String.fromCharCode(letter.charCodeAt(0) + 1);
  }
};

const muiControls = [
  toolbarControlTypes.undo,
  toolbarControlTypes.redo,
  toolbarControlTypes.divider,
  toolbarControlTypes.bold,
  toolbarControlTypes.italic,
  toolbarControlTypes.underline,
  toolbarControlTypes.strikethrough,
  toolbarControlTypes.fontColor,
  toolbarControlTypes.fontBackgroundColor,
  toolbarControlTypes.divider,
  toolbarControlTypes.linkAdd,
  toolbarControlTypes.linkRemove,
  // toolbarControlTypes.image,
  toolbarControlTypes.divider,
  toolbarControlTypes.blockType,
  toolbarControlTypes.fontSize,
  toolbarControlTypes.fontFamily,
  toolbarControlTypes.textAlign,
  toolbarControlTypes.divider,
  toolbarControlTypes.unorderedList,
  toolbarControlTypes.orderedList,
];

export {
  axiosConfig,
  dateTimeView,
  dateView,
  existValue,
  getActionType,
  getDesignationType,
  getReviewStatus,
  getTicketStatus,
  getTicketType,
  getTypeFromNameFile,
  handleErrorMessage,
  handleSuccessMessage,
  isUserAssociateEditor,
  muiControls,
  parseArticleForBackend,
  parseDate,
  preventDefaultEnter,
  renameFile,
  richTextStateFromHTML,
  richTextStateToHtml,
  richTextValue,
  sortArticleArray,
  uniqueBy,
  useEffectOnce,
  validateEmail,
};
